import { useEffect, useState, useCallback } from "react";
import { Download, RefreshCw, ScrollText, Filter } from "lucide-react";
import { api, formatApiError, API_ROOT } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function AuditPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState("any");
  const [event, setEvent] = useState("any");
  const [days, setDays] = useState("30");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (success !== "any") params.success = success;
      if (event !== "any") params.event = event;
      if (days && days !== "all") params.days = Number(days);
      if (q) params.q = q;
      const { data } = await api.get("/audit", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [success, event, days, q]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const url = new URL(`${API_ROOT}/audit/export`);
    if (success !== "any") url.searchParams.set("success", success);
    if (event !== "any") url.searchParams.set("event", event);
    if (days && days !== "all") url.searchParams.set("days", days);
    if (q) url.searchParams.set("q", q);
    window.open(url.toString(), "_blank", "noopener");
  };

  return (
    <div>
      <PageHeader
        title="Audit Log"
        description="Every SSO attempt, provisioning ticket, and helpdesk shadow-login is captured here."
        testId="audit-header"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={load} data-testid="audit-refresh-btn">
              <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
            </Button>
            <Button size="sm" onClick={exportCsv} data-testid="audit-export-btn">
              <Download className="mr-1.5 h-4 w-4" /> Export CSV
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={success} onValueChange={setSuccess}>
          <SelectTrigger className="w-40" data-testid="audit-success-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any outcome</SelectItem>
            <SelectItem value="success">Success only</SelectItem>
            <SelectItem value="failure">Failures only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={event} onValueChange={setEvent}>
          <SelectTrigger className="w-44" data-testid="audit-event-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">All events</SelectItem>
            <SelectItem value="sso_login">SSO login</SelectItem>
            <SelectItem value="shadow_login">Shadow login</SelectItem>
            <SelectItem value="provision_request">Provisioning</SelectItem>
          </SelectContent>
        </Select>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-32" data-testid="audit-days-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 24h</SelectItem>
            <SelectItem value="7">Last 7d</SelectItem>
            <SelectItem value="30">Last 30d</SelectItem>
            <SelectItem value="90">Last 90d</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
        <Input
          className="w-64"
          placeholder="Search user / IP / message…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="audit-search-input"
        />
        <Button variant="outline" size="sm" onClick={load} data-testid="audit-apply-btn">Apply</Button>
      </div>

      {loading ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="No audit entries match"
          description="Try changing the filters, or send a test SSO handoff to see it appear here."
          testId="audit-empty"
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                <th className="px-4 py-2.5">Time</th>
                <th className="px-4 py-2.5">Event</th>
                <th className="px-4 py-2.5">Outcome</th>
                <th className="px-4 py-2.5">Halo ref</th>
                <th className="px-4 py-2.5">cPanel</th>
                <th className="px-4 py-2.5">WHM server</th>
                <th className="px-4 py-2.5">Source IP</th>
                <th className="px-4 py-2.5">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-muted/40" data-testid={`audit-row-${r.id}`}>
                  <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2.5 text-xs">{r.event}</td>
                  <td className="px-4 py-2.5"><StatusPill status={r.success ? "success" : "failure"} /></td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.halo_user_ref || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.cpanel_username || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{r.whm_server_label || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.source_ip}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
